import React, { useState } from "react";
import {
  Edit3,
  Trash2,
  Book,
  Award,
  Clock,
  Target,
  MoreVertical,
  Users,
} from "lucide-react";
import { Course } from "../../types/course";

interface CoursesTableProps {
  courses: Course[];
  loading: boolean;
  onEdit: (course: Course) => void;
  onDelete: (courseId: string) => void;
  onBulkDelete: (courseIds: string[]) => void;
}

const CoursesTable: React.FC<CoursesTableProps> = ({
  courses,
  loading,
  onEdit,
  onDelete,
  onBulkDelete,
}) => {
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<
    "name" | "level" | "credits" | "category"
  >("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSelectAll = (checked: boolean) => {
    setSelectedCourses(checked ? courses.map((c) => c._id) : []);
  };

  const handleSelectCourse = (courseId: string, checked: boolean) => {
    setSelectedCourses((prev) =>
      checked ? [...prev, courseId] : prev.filter((id) => id !== courseId)
    );
  };

  const handleSort = (field: "name" | "level" | "credits" | "category") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortedCourses = [...courses].sort((a, b) => {
    let aValue: string | number = "";
    let bValue: string | number = "";

    switch (sortBy) {
      case "name":
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case "level":
        aValue = a.level;
        bValue = b.level;
        break;
      case "credits":
        aValue = a.credits;
        bValue = b.credits;
        break;
      case "category":
        aValue = a.category;
        bValue = b.category;
        break;
    }

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getCategoryBadge = (category: string) => {
    const badges = {
      core: "badge-primary",
      elective: "badge-secondary",
      practical: "badge-accent",
      theory: "badge-neutral",
    };
    return badges[category as keyof typeof badges] || "badge-ghost";
  };

  const getLevelBadge = (level: string) => {
    const badges = {
      Begginner: "badge-success",
      Intermediate: "badge-warning",
      Advanced: "badge-error",
    };
    return badges[level as keyof typeof badges] || "badge-ghost";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedCourses.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
          <span className="font-bold text-slate-900">
            {selectedCourses.length} course(s) selected
          </span>
          <button
            onClick={() => onBulkDelete(selectedCourses)}
            className="bg-white border-2 border-slate-200 hover:border-red-300 text-slate-700 px-4 py-2 rounded-xl font-bold flex items-center gap-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
            <tr>
              <th className="px-6 py-4">
                <label>
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-2 focus:ring-blue-400"
                    checked={selectedCourses.length === courses.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </label>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-2">
                  <Book className="h-4 w-4 text-blue-500" />
                  Course Name
                  {sortBy === "name" && (
                    <span className="text-blue-500 font-bold">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                onClick={() => handleSort("level")}
              >
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-500" />
                  Level
                  {sortBy === "level" && (
                    <span className="text-purple-500 font-bold">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                onClick={() => handleSort("category")}
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-emerald-500" />
                  Category
                  {sortBy === "category" && (
                    <span className="text-emerald-500 font-bold">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                onClick={() => handleSort("credits")}
              >
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-500" />
                  Credits
                  {sortBy === "credits" && (
                    <span className="text-amber-500 font-bold">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  Duration
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {sortedCourses.map((course) => (
              <tr
                key={course._id}
                className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all duration-200"
              >
                <td className="px-6 py-4">
                  <label>
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-2 focus:ring-blue-400"
                      checked={selectedCourses.includes(course._id)}
                      onChange={(e) =>
                        handleSelectCourse(course._id, e.target.checked)
                      }
                    />
                  </label>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-bold text-slate-900">
                      {course.name}({course.level})
                    </div>
                    {course.description && (
                      <div className="text-sm text-slate-600 font-medium truncate max-w-xs mt-1">
                        {course.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl ${
                      course.level === "Begginner"
                        ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                        : course.level === "Intermediate"
                        ? "bg-gradient-to-br from-amber-300 to-amber-400 text-white"
                        : "bg-gradient-to-br from-red-300 to-red-400 text-white"
                    }`}
                  >
                    {course.level}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl ${
                      course.category === "core"
                        ? "bg-gradient-to-br from-blue-300 to-blue-400 text-white"
                        : course.category === "elective"
                        ? "bg-gradient-to-br from-purple-300 to-purple-400 text-white"
                        : course.category === "practical"
                        ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                        : "bg-gradient-to-br from-slate-300 to-slate-400 text-white"
                    }`}
                  >
                    {course.category}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-500" />
                    <span className="font-bold text-slate-900">
                      {course.credits}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {course.duration ? (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-500" />
                      <span className="font-semibold text-slate-900">
                        {course.duration && typeof course.duration === "object"
                          ? `${(course.duration as any).totalHours || 0}h`
                          : `${course.duration}h`}
                      </span>
                    </div>
                  ) : (
                    <span className="text-slate-400 font-medium">-</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl ${
                      course.isActive
                        ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                        : "bg-gradient-to-br from-red-300 to-red-400 text-white"
                    }`}
                  >
                    {course.isActive ? "Active" : "Inactive"}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(course)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                      title="Edit Course"
                    >
                      <Edit3 className="h-5 w-5 text-amber-500" />
                    </button>
                    <button
                      onClick={() => onDelete(course._id)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200"
                      title="Delete Course"
                    >
                      <Trash2 className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {courses.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Book className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No courses found
            </h3>
            <p className="text-slate-600">
              Create your first course to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesTable;
